class LogoCustomizer extends HTMLElement {
  constructor() {
    super();
    this.formId = this.dataset.formId;
    this.maxFileBytes = (Number(this.dataset.maxFileMb) || 10) * 1024 * 1024;
    // Shopify caps native line item attachments at 20 MB per file.
    this.maxVectorBytes = (Number(this.dataset.maxVectorMb) || 20) * 1024 * 1024;
    this.maxLogos = Number(this.dataset.maxLogos) || 5;
    this.logoProperty = this.dataset.logoProperty || 'Logo';
    this.requireUpload = this.dataset.requireUpload === 'true';
    this.allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];

    this.logos = [];
    this.activeId = null;
    this.nextId = 1;
    this.manualBase = false;
    this.composeToken = 0;
    this.composePending = false;
    this.composeTimer = null;
    this.composeBasePromise = null;
    this.hintDismissed = false;
  }

  connectedCallback() {
    this.stage = this.querySelector('[data-stage]');
    this.baseImg = this.querySelector('.logo-customizer__base');
    this.hint = this.querySelector('[data-hint]');
    this.errorEl = this.querySelector('[data-error]');
    this.addInput = this.querySelector('[data-file-input]');
    this.addWrap = this.querySelector('[data-add-wrap]');
    this.removeBtn = this.querySelector('[data-remove]');
    this.slidersWrap = this.querySelector('[data-sliders-wrap]');
    this.scaleInput = this.querySelector('[data-scale]');
    this.rotateInput = this.querySelector('[data-rotate]');
    this.noteEl = this.querySelector('[data-note]');
    this.previewInput = this.querySelector('[data-preview-input]');
    this.placementInput = this.querySelector('[data-placement-input]');
    this.logoInputsWrap = this.querySelector('[data-logo-inputs]');
    this.downloadBtn = this.querySelector('[data-download]');
    this.mobileDisclaimer = this.querySelector('.logo-customizer__disclaimer--mobile');
    this.toggleBtn = this.querySelector('[data-toggle]');
    this.modal = this.querySelector('[data-modal]');
    this.toggleStatus = this.querySelector('[data-toggle-status]');
    this.reuseStatus = this.querySelector('[data-reuse-status]');
    this.thumbs = [...this.querySelectorAll('[data-base-thumb]')];
    this.mediaGrouping = this.dataset.mediaGrouping === 'true';
    this.groupOptionIndex = Number(this.dataset.groupOptionIndex) || 0;

    if (!this.stage || !this.addInput || !this.previewInput) return;

    // Ensure file uploads survive a native (no-JS) form post too.
    const form = this.getForm();
    if (form) form.enctype = 'multipart/form-data';

    this.toggleBtn?.addEventListener('click', () => this.openModal());
    this.querySelectorAll('[data-modal-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeModal());
    });
    this.addInput.addEventListener('change', this.handleAddFile.bind(this));
    this.removeBtn?.addEventListener('click', () => this.removeLogo(this.activeId));
    this.downloadBtn?.addEventListener('click', () => this.downloadPreview());

    this.scaleInput?.addEventListener('input', () => {
      const logo = this.getActive();
      if (!logo) return;
      logo.scale = Number(this.scaleInput.value) / 100;
      this.clampPosition(logo);
      this.renderLogo(logo);
    });
    this.rotateInput?.addEventListener('input', () => {
      const logo = this.getActive();
      if (!logo) return;
      logo.rotation = Number(this.rotateInput.value);
      this.clampPosition(logo);
      this.renderLogo(logo);
    });
    this.scaleInput?.addEventListener('change', () => this.scheduleCompose());
    this.rotateInput?.addEventListener('change', () => this.scheduleCompose());

    // Dragging is delegated from the stage so every logo layer is draggable;
    // pressing a logo also makes it the active one for the sliders.
    this.stage.addEventListener('pointerdown', this.handleDragStart.bind(this));

    // Customer-facing base image picker.
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        this.manualBase = true;
        this.setBase(thumb.dataset.display, thumb.dataset.compose);
      });
    });

    // Follow variant changes (same image as the media gallery) until the
    // customer picks a base image manually or a mockup metafield is set.
    const variantImagesScript = this.querySelector('[data-variant-images]');
    if (variantImagesScript) {
      try {
        this.variantImages = JSON.parse(variantImagesScript.textContent);
      } catch (error) {
        this.variantImages = null;
      }
    }

    // Attach the variant listener when either the base image should follow the
    // variant, or the thumbnails should be filtered to the selected variant.
    const sectionEl = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
    (sectionEl || document).addEventListener('on:variant:change', this.handleVariantChange.bind(this));
    (sectionEl || document).addEventListener('on:cart:add', () => this.refreshGroupState());
    this.refreshGroupState();

    // Hold the submit while a preview compose is pending; also enforce the
    // "require at least one logo" setting here (the add input is no longer a
    // form control, so native validation can't do it).
    this.submitGuard = (evt) => {
      if (evt.target !== this.getForm()) return;
      if (this.requireUpload && !this.logos.length && !this.reusesExistingGroup) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.openModal();
        this.setError(this.dataset.errorRequired || 'Add a logo first.');
        return;
      }
      if (!this.logos.length || !this.composePending) return;
      evt.preventDefault();
      evt.stopImmediatePropagation();
      this.composeNow().finally(() => {
        this.getForm()?.requestSubmit(evt.submitter || null);
      });
    };
    document.addEventListener('submit', this.submitGuard, true);

    // Authored inside this element for readability, but the sticky product-info
    // column creates a stacking context that would trap the fixed overlay
    // behind the header — so the modal is reparented to the body. Must run
    // after every querySelector above, which relies on it being a descendant.
    if (this.modal && this.modal.parentElement !== document.body) {
      document.body.appendChild(this.modal);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.submitGuard, true);
    // Releases the focus trap and body scroll lock if the section re-renders
    // (e.g. theme editor, variant change) while the modal is open.
    this.closeModal();
    // Take the modal back so it is torn down with this element, and so a
    // re-connect (theme editor) still finds it in the subtree.
    if (this.modal && this.modal.parentElement === document.body) {
      this.appendChild(this.modal);
    }
    this.logos.forEach((logo) => URL.revokeObjectURL(logo.url));
  }

  getForm() {
    return document.getElementById(this.formId);
  }

  getActive() {
    return this.logos.find((logo) => logo.id === this.activeId) || null;
  }

  /**
   * Identity of the chosen artwork, independent of the Shopify upload URLs —
   * re-uploading the same file always yields a fresh URL, so the cart can only
   * recognise a repeat order through the file contents. Placement is left out
   * on purpose: moving a logo does not make it a different logo, the newest
   * placement simply wins.
   * @returns {Promise<string>} Empty when nothing is uploaded.
   */
  async getLogoFileKey() {
    const files = this.logos.map((logo) => logo.file).filter(Boolean);
    if (!files.length) return '';

    const hashes = await Promise.all(files.map((file) => LogoCustomizer.fileKey(file)));
    return hashes.filter(Boolean).sort().join('.');
  }

  static fileKey(file) {
    if (!LogoCustomizer.fileKeys) LogoCustomizer.fileKeys = new WeakMap();
    const cached = LogoCustomizer.fileKeys.get(file);
    if (cached) return cached;

    const pending = (async () => {
      try {
        const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
        return [...new Uint8Array(digest)]
          .map((byte) => byte.toString(16).padStart(2, '0'))
          .join('')
          .slice(0, 32);
      } catch (error) {
        // SubtleCrypto needs a secure context; name/size/mtime is close enough.
        return `${file.name}:${file.size}:${file.lastModified}`;
      }
    })();
    LogoCustomizer.fileKeys.set(file, pending);
    return pending;
  }

  async refreshGroupState() {
    const form = this.getForm();
    if (!form || typeof theme.hintaportaat?.fetchCart !== 'function') return;

    try {
      const formData = new FormData(form);
      const productId = form.dataset.productId;
      const colorGroup = String(formData.get('properties[_color_group]') || '').trim();
      const section = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const hasLisapalvelu =
        form.dataset.lisapalveluProductGroup === 'true'
        || Boolean(section?.querySelector('lisavaruste-picker'));
      if (!hasLisapalvelu) {
        this.reusesExistingGroup = false;
        if (this.reuseStatus) this.reuseStatus.hidden = true;
        return;
      }
      const addonVariantIds = [...(section?.querySelectorAll('[data-lisavaruste-item]') || [])]
        .map((item) => Number(item.dataset.variantId))
        .filter(Boolean);
      const cart = await theme.hintaportaat.fetchCart();
      const group = theme.hintaportaat.serviceGroupState(
        cart,
        productId,
        colorGroup,
        addonVariantIds,
        { scope: 'product' }
      );
      this.reusesExistingGroup = group.exists;
      if (this.reuseStatus) this.reuseStatus.hidden = !group.exists;
    } catch {
      this.reusesExistingGroup = false;
      if (this.reuseStatus) this.reuseStatus.hidden = true;
    }
  }

  setError(message = null) {
    if (!this.errorEl) return;
    this.errorEl.hidden = !message;
    this.errorEl.textContent = message || '';
  }

  openModal() {
    if (!this.modal || this.modal.hasAttribute('open')) return;

    if (typeof this.modal.open === 'function') {
      this.modal.open(this.toggleBtn);
    } else {
      this.modal.setAttribute('open', '');
    }

    // The stage is scaled/offset while the modal animates in — re-clamp once
    // the window has settled at its final size.
    if (this.logos.length) {
      requestAnimationFrame(() => {
        this.logos.forEach((logo) => {
          this.clampPosition(logo);
          this.renderLogo(logo);
        });
      });
    }
  }

  closeModal() {
    if (!this.modal) return;

    if (typeof this.modal.close === 'function') {
      this.modal.close();
    } else {
      this.modal.removeAttribute('open');
    }
  }

  updateToggleStatus() {
    if (!this.toggleStatus) return;
    const count = this.logos.length;
    this.toggleStatus.hidden = count === 0;
    this.toggleBtn?.classList.toggle('is-attached', count > 0);
    if (count > 0) {
      const word = count === 1
        ? (this.dataset.attachedOne || 'logo added')
        : (this.dataset.attachedOther || 'logos added');
      this.toggleStatus.textContent = `${count} ${word}`;
    }
  }

  /* ------------------------------------------------------------------ *
   * Base image
   * ------------------------------------------------------------------ */

  setBase(display, compose) {
    if (!compose || compose === this.dataset.composeSrc) {
      this.syncThumbState(compose);
      return;
    }
    this.dataset.composeSrc = compose;
    this.composeBasePromise = null;
    if (this.baseImg) this.baseImg.src = display;
    this.syncThumbState(compose);
    if (this.logos.length) {
      // Aspect ratio may change; keep every logo inside the new stage.
      this.logos.forEach((logo) => {
        this.clampPosition(logo);
        this.renderLogo(logo);
      });
      this.scheduleCompose(0);
    }
  }

  syncThumbState(compose) {
    this.thumbs.forEach((thumb) => {
      const active = thumb.dataset.compose === compose;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  handleVariantChange(evt) {
    const variant = evt.detail && evt.detail.variant;
    if (!variant) return;
    this.refreshGroupState();

    // Filter the base-image thumbnails to the selected variant's group first,
    // regardless of whether a base image was picked manually.
    let activeHidden = false;
    let firstVisible = null;
    if (this.mediaGrouping) {
      ({ activeHidden, firstVisible } = this.filterThumbsByVariant(variant));
    }

    if (!this.manualBase && this.variantImages) {
      const images = this.variantImages[variant.id] || this.variantImages.default;
      if (images) this.setBase(images.display, images.compose);
    } else if (activeHidden && firstVisible) {
      // The current base image is no longer in the visible group — fall back to
      // the first thumbnail that is still shown.
      this.setBase(firstVisible.dataset.display, firstVisible.dataset.compose);
    }
  }

  filterThumbsByVariant(variant) {
    const groupValue = (variant.options && variant.options[this.groupOptionIndex]) || '';
    let activeHidden = false;
    let firstVisible = null;

    this.thumbs.forEach((thumb) => {
      const group = thumb.dataset.group;
      // Thumbs without a group (admin mockup or ungrouped media) always show.
      const visible = !group || group === groupValue;
      thumb.hidden = !visible;
      if (visible) {
        if (!firstVisible) firstVisible = thumb;
      } else if (thumb.classList.contains('is-active')) {
        activeHidden = true;
      }
    });

    return { activeHidden, firstVisible };
  }

  /* ------------------------------------------------------------------ *
   * Logo layers
   * ------------------------------------------------------------------ */

  handleAddFile() {
    this.setError();
    const file = this.addInput.files && this.addInput.files[0];
    this.addInput.value = '';
    if (!file) return;

    if (this.logos.length >= this.maxLogos) {
      this.setError(this.dataset.errorMax || 'Maximum number of logos reached.');
      return;
    }
    if (!this.allowedTypes.includes(file.type)) {
      this.setError(this.dataset.errorType || 'Unsupported file type.');
      return;
    }
    if (file.size > this.maxFileBytes) {
      this.setError(this.dataset.errorSize || 'File is too large.');
      return;
    }

    const url = URL.createObjectURL(file);
    const wrap = document.createElement('div');
    wrap.className = 'logo-customizer__logo';
    const img = document.createElement('img');
    img.alt = '';
    img.draggable = false;
    wrap.appendChild(img);

    img.onload = () => {
      const logo = {
        id: this.nextId++,
        file,
        url,
        img,
        wrap,
        ratio: img.naturalWidth > 0 ? img.naturalHeight / img.naturalWidth : 1,
        x: 0.5,
        y: 0.5,
        scale: 0.3,
        rotation: 0,
        input: this.createLogoInput(file),
      };
      // Offset stacked logos slightly so a new one is never fully hidden.
      const offset = (this.logos.length % 3) * 0.08;
      logo.x = Math.min(0.9, 0.5 + offset);
      logo.y = Math.min(0.9, 0.42 + offset);

      this.logos.push(logo);
      this.stage.insertBefore(wrap, this.hint);
      wrap.dataset.logoId = String(logo.id);

      this.clampPosition(logo);
      this.renderLogo(logo);
      this.selectLogo(logo.id);
      this.syncLogoInputs();
      this.updateUiState();
      if (this.hint && !this.hintDismissed) this.hint.hidden = false;
      this.scheduleCompose(0);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      this.setError(this.dataset.errorType || 'Unsupported file type.');
    };
    img.src = url;
  }

  createLogoInput(file) {
    const input = document.createElement('input');
    input.type = 'file';
    input.hidden = true;
    input.tabIndex = -1;
    input.setAttribute('aria-hidden', 'true');
    input.dataset.serviceLogoProperty = 'true';
    input.setAttribute('form', this.formId);
    const transfer = new DataTransfer();
    transfer.items.add(file);
    input.files = transfer.files;
    this.logoInputsWrap?.appendChild(input);
    return input;
  }

  syncLogoInputs() {
    // Property names stay sequential (Logo 1, Logo 2, …) even after removals.
    this.logos.forEach((logo, index) => {
      logo.input.name = `properties[${this.logoProperty} ${index + 1}]`;
    });
  }

  removeLogo(id) {
    const index = this.logos.findIndex((logo) => logo.id === id);
    if (index === -1) return;
    const [logo] = this.logos.splice(index, 1);
    URL.revokeObjectURL(logo.url);
    logo.wrap.remove();
    logo.input.remove();
    this.syncLogoInputs();

    if (this.activeId === id) {
      const next = this.logos[this.logos.length - 1];
      this.activeId = null;
      if (next) this.selectLogo(next.id);
    }
    this.updateUiState();
    this.setError();

    if (this.logos.length) {
      this.scheduleCompose(0);
    } else {
      this.clearOutputs();
    }
  }

  clearOutputs() {
    this.composeToken += 1;
    this.composePending = false;
    if (this.composeTimer) clearTimeout(this.composeTimer);
    this.previewInput.files = new DataTransfer().files;
    this.previewInput.removeAttribute('name');
    if (this.placementInput) {
      this.placementInput.value = '';
      this.placementInput.removeAttribute('name');
    }
  }

  selectLogo(id) {
    this.activeId = id;
    this.logos.forEach((logo) => {
      logo.wrap.classList.toggle('is-active', logo.id === id);
    });
    const active = this.getActive();
    if (active) {
      if (this.scaleInput) this.scaleInput.value = String(Math.round(active.scale * 100));
      if (this.rotateInput) this.rotateInput.value = String(Math.round(active.rotation));
    }
  }

  updateUiState() {
    const count = this.logos.length;
    if (this.removeBtn) this.removeBtn.hidden = count === 0;
    if (this.slidersWrap) this.slidersWrap.hidden = count === 0;
    if (this.noteEl) this.noteEl.hidden = count === 0;
    if (this.addWrap) this.addWrap.hidden = count >= this.maxLogos;
    if (this.downloadBtn) this.downloadBtn.hidden = count === 0;
    if (this.mobileDisclaimer) this.mobileDisclaimer.hidden = count === 0;
    if (this.hint && count === 0) this.hint.hidden = true;
    this.classList.toggle('logo-customizer--has-logos', count > 0);
    this.updateToggleStatus();
  }

  /* ------------------------------------------------------------------ *
   * Dragging & geometry
   * ------------------------------------------------------------------ */

  handleDragStart(evt) {
    const wrap = evt.target.closest('.logo-customizer__logo');
    if (!wrap) return;
    const logo = this.logos.find((l) => String(l.id) === wrap.dataset.logoId);
    if (!logo) return;
    evt.preventDefault();

    if (this.hint) {
      this.hint.hidden = true;
      this.hintDismissed = true;
    }
    this.selectLogo(logo.id);

    const stageRect = this.stage.getBoundingClientRect();
    const grabOffsetX = evt.clientX - (stageRect.left + logo.x * stageRect.width);
    const grabOffsetY = evt.clientY - (stageRect.top + logo.y * stageRect.height);

    wrap.classList.add('is-dragging');

    const onMove = (moveEvt) => {
      const rect = this.stage.getBoundingClientRect();
      logo.x = (moveEvt.clientX - grabOffsetX - rect.left) / rect.width;
      logo.y = (moveEvt.clientY - grabOffsetY - rect.top) / rect.height;
      this.clampPosition(logo);
      this.renderLogo(logo);
    };

    const onEnd = (endEvt) => {
      wrap.classList.remove('is-dragging');
      wrap.removeEventListener('pointermove', onMove);
      wrap.removeEventListener('pointerup', onEnd);
      wrap.removeEventListener('pointercancel', onEnd);
      try {
        if (wrap.hasPointerCapture(endEvt.pointerId)) {
          wrap.releasePointerCapture(endEvt.pointerId);
        }
      } catch (error) {
        // ignore — capture may never have been established
      }
      this.scheduleCompose();
    };

    wrap.addEventListener('pointermove', onMove);
    wrap.addEventListener('pointerup', onEnd);
    wrap.addEventListener('pointercancel', onEnd);

    try {
      wrap.setPointerCapture(evt.pointerId);
    } catch (error) {
      // drag still works without pointer capture
    }
  }

  clampPosition(logo) {
    const stageRect = this.stage.getBoundingClientRect();
    if (!stageRect.width || !stageRect.height) return;

    // Clamp using the axis-aligned bounding box of the rotated logo.
    const w = logo.scale;
    const h = (logo.scale * stageRect.width * logo.ratio) / stageRect.height;
    const rad = (logo.rotation * Math.PI) / 180;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));
    const bboxW = w * cos + (h * stageRect.height * sin) / stageRect.width;
    const bboxH = (w * stageRect.width * sin) / stageRect.height + h * cos;

    logo.x = bboxW >= 1 ? 0.5 : Math.min(Math.max(logo.x, bboxW / 2), 1 - bboxW / 2);
    logo.y = bboxH >= 1 ? 0.5 : Math.min(Math.max(logo.y, bboxH / 2), 1 - bboxH / 2);
  }

  renderLogo(logo) {
    logo.wrap.style.left = `${logo.x * 100}%`;
    logo.wrap.style.top = `${logo.y * 100}%`;
    logo.wrap.style.width = `${logo.scale * 100}%`;
    logo.wrap.style.transform = `translate(-50%, -50%) rotate(${logo.rotation}deg)`;
  }

  /* ------------------------------------------------------------------ *
   * Compose
   * ------------------------------------------------------------------ */

  scheduleCompose(delay = 250) {
    this.composePending = true;
    if (this.composeTimer) clearTimeout(this.composeTimer);
    this.composeTimer = setTimeout(() => this.composeNow(), delay);
  }

  getComposeBase() {
    if (!this.composeBasePromise) {
      this.composeBasePromise = new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => {
          this.composeBasePromise = null;
          reject(new Error('base image failed to load'));
        };
        img.src = this.dataset.composeSrc;
      });
    }
    return this.composeBasePromise;
  }

  async composePreviewBlob() {
    if (!this.logos.length) return null;

    const base = await this.getComposeBase();
    const cap = 2400;
    const downscale = Math.min(1, cap / Math.max(base.naturalWidth, base.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(base.naturalWidth * downscale);
    canvas.height = Math.round(base.naturalHeight * downscale);

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(base, 0, 0, canvas.width, canvas.height);

    this.logos.forEach((logo) => {
      const logoW = logo.scale * canvas.width;
      const logoH = logoW * logo.ratio;
      ctx.save();
      ctx.translate(logo.x * canvas.width, logo.y * canvas.height);
      ctx.rotate((logo.rotation * Math.PI) / 180);
      ctx.drawImage(logo.img, -logoW / 2, -logoH / 2, logoW, logoH);
      ctx.restore();
    });

    return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
  }

  async composeNow() {
    if (this.composeTimer) clearTimeout(this.composeTimer);
    if (!this.logos.length) {
      this.composePending = false;
      return;
    }

    const token = (this.composeToken += 1);

    try {
      const blob = await this.composePreviewBlob();
      if (token !== this.composeToken || !blob) return;

      const file = new File([blob], this.dataset.previewFilename || 'esikatselu.jpg', { type: 'image/jpeg' });
      const transfer = new DataTransfer();
      transfer.items.add(file);
      this.previewInput.files = transfer.files;
      this.previewInput.name = this.previewInput.dataset.name;

      if (this.placementInput) {
        this.placementInput.value = this.logos
          .map((logo, index) => [
            `${this.logoProperty} ${index + 1}:`,
            `x ${Math.round(logo.x * 100)}%,`,
            `y ${Math.round(logo.y * 100)}%,`,
            `leveys ${Math.round(logo.scale * 100)}%,`,
            `kulma ${Math.round(logo.rotation)}°`,
          ].join(' '))
          .join('; ');
        this.placementInput.name = this.placementInput.dataset.name;
      }
    } catch (error) {
      // Preview could not be composed (e.g. image CORS failure). The original
      // logo files and placements still go through with the order.
      console.warn('logo-customizer: preview compose failed', error);
    } finally {
      if (token === this.composeToken) this.composePending = false;
    }
  }

  async downloadPreview() {
    this.setError();
    if (!this.logos.length) {
      this.setError(this.dataset.errorDownload || 'Add a logo first.');
      return;
    }

    try {
      const blob = await this.composePreviewBlob();
      if (!blob) {
        this.setError(this.dataset.errorDownload || 'Could not create preview.');
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = this.dataset.previewFilename || 'esikatselu.jpg';
      link.click();
      URL.revokeObjectURL(url);
      await this.composeNow();
    } catch (error) {
      console.warn('logo-customizer: download failed', error);
      this.setError(this.dataset.errorDownload || 'Could not create preview.');
    }
  }
}

if (!customElements.get('logo-customizer')) {
  customElements.define('logo-customizer', LogoCustomizer);
}
